import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import BreBusinessCardsConfig, {
  BusinessCardPaper
} from '~/tenants/bre/performable/print/cards/BreBusinessCardsConfig';
import { BreBusinessCardsPay, BreBusinessCardsRate } from '~/tenants/bre/performable/print/cards/BreBusinessCardsData';
import { datetime } from '~/lib/datettime';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';

export default class BreBusinessCardsJob extends BreJob<typeof BreBusinessCardsConfig> {
  get performable(): typeof BreBusinessCardsConfig {
    return BreBusinessCardsConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.AGENT_MKTG);
  }

  get paperType(): string {
    const { paper } = this.metadata;
    return BreBusinessCardsJob.paperType(paper);
  }

  static paperType(paper: BusinessCardPaper): string {
    return match(paper)
      .with('uv_glossy', () => 'UV glossy coated')
      .with('satin_matte', () => 'Satin Matte coated ')
      .exhaustive();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Count',
      value: this.metadata.count,
      provider: true,
    });

    info.push({
      name: 'Paper',
      value: this.paperType,
      provider: true,
    });

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  isDelivery(): boolean {
    return true;
  }

  isPrint(): boolean {
    return true;
  }

  isFreeDelivery(): boolean {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreBusinessCardsConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      count: '50',
      paper: 'uv_glossy',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate()
    };
  }

  revenueLines(): TenantJobLine[] {
    const { count, paper } = this.metadata;
    const rate = BreBusinessCardsRate[paper][count];
    const lines: TenantJobLine[] = [];

    if (rate) {
      lines.push({
        id: 'business_cards_print',
        description: `${this.paperType} business cards (x${count})`,
        amount: new Big(rate),
        taxable: true,
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { count, paper } = this.metadata;
    const pay = BreBusinessCardsPay[paper][count];
    const lines: TenantJobLine[] = [];

    if (pay) {
      lines.push({
        id: 'business_cards_print',
        description: `${this.paperType} business cards (x${count})`,
        amount: new Big(pay),
      });
    }

    return lines;
  }
}
